import { useRef, useState, type ChangeEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Color, HR_EMPLOYEES_ROUTE, Size, UNEMPLOYMENT_CLAIM_OUTCOMES, UNEMPLOYMENT_CLAIMS_ROUTE, UNEMPLOYMENT_CONTEST_DECISIONS, claimDaysLeft, claimOverdue,
  employeeRoute, formatDateOnly, pageKindLineText, t, unemploymentClaimPacketPath, unemploymentClaimPath, unemploymentClaimProblem, unemploymentClaimsPath,
  unemploymentClaimUpdateProblem, type StringKey, type UnemploymentClaimRow, type UnemploymentClaimsAnswer,
} from '@bms2/shared';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { Card } from '../../components/composites';
import { Button, DatePicker, Select, TextInput, Textarea } from '../../components/controls';
import { useNotice } from '../../components/controls/useNotice';
import { PageModule } from '../../shell/PageModule';
import { useDateFormat } from '../../shell/ViewerZone';
import { post, upload, useApi } from '../records/api';
import { line } from '../settings/SettingsShell';
import { HrPagesNav } from './HrPagesNav';
import styles from './HrPages.module.css';

export { UNEMPLOYMENT_CLAIMS_ROUTE };

const lowerFirst = (value: string): string => `${value.charAt(0).toLowerCase()}${value.slice(1)}`;
const decisionLabel = (value: string): string => t(`hr.claims.decision.${lowerFirst(value)}` as StringKey);
const outcomeLabel = (value: string): string => t(`hr.claims.outcome.${lowerFirst(value)}` as StringKey);

/**
 * One job's unemployment claims (piece 34; decision 10651; T885), under its
 * Employee page. 🔒 HR records the state, the notice date and the response
 * deadline from the state's notice, then the contest decision and the outcome,
 * and uploads the contest packet onto the claim. The route asks the hire
 * permission, so the employee and the chain are refused in its sentence.
 */
export function UnemploymentClaimsPage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const { employmentId = '' } = useParams();
  const fetched = useApi<UnemploymentClaimsAnswer>(employmentId ? unemploymentClaimsPath(employmentId) : null, apiBaseUrl);
  const [noticeBanner, setNotice] = useNotice();
  const title = fetched.state.kind === 'ready' ? line(t('hr.claims.title'), '·', fetched.state.data.personName) : t('hr.claims.title');
  return (
    <PageModule title={title} subtitle={pageKindLineText({ page: 'named', route: UNEMPLOYMENT_CLAIMS_ROUTE })} icon="hr">
      <div className={styles.page} data-page="hr-unemployment-claims">
        <HrPagesNav current={HR_EMPLOYEES_ROUTE} />
        <Link to={employeeRoute(employmentId)} data-claims-back="true">{t('hr.claims.back')}</Link>
        {noticeBanner}
        <AsyncStatePanel state={fetched.state} emptyAction={t('hr.claims.empty')} onRetry={fetched.reload}>
          {(answer) => (
            <div data-unemployment-claims={String(answer.rows.length)}>
              <NewClaim apiBaseUrl={apiBaseUrl} employmentId={employmentId} onRecorded={() => { setNotice({ tone: 'good', text: t('hr.claims.recorded') }); fetched.reload(); }} />
              {answer.rows.length === 0 ? <p className={styles.meta} data-claims-empty="true">{t('hr.claims.empty')}</p> : null}
              {answer.rows.map((row) => (
                <ClaimCard
                  key={`${row.id}:${row.version}`}
                  row={row}
                  today={answer.today}
                  apiBaseUrl={apiBaseUrl}
                  onChanged={(text) => { setNotice({ tone: 'good', text }); fetched.reload(); }}
                  onRefused={(text) => setNotice({ tone: 'error', text })}
                />
              ))}
            </div>
          )}
        </AsyncStatePanel>
      </div>
    </PageModule>
  );
}

/** Record a Claim: the state, the notice date and the deadline, each refused before it is sent in the server's own sentence. */
function NewClaim({ apiBaseUrl, employmentId, onRecorded }: { apiBaseUrl: string; employmentId: string; onRecorded: () => void }) {
  const [stateCode, setStateCode] = useState('');
  const [claimDate, setClaimDate] = useState('');
  const [responseDueOn, setResponseDueOn] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const record = async () => {
    const draft = { stateCode, claimDate, responseDueOn };
    const problem = unemploymentClaimProblem(draft);
    if (problem) return setError(problem);
    setBusy(true);
    const answer = await post<UnemploymentClaimsAnswer>(unemploymentClaimsPath(employmentId), draft, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setError(answer.message);
    setError(null);
    setStateCode('');
    setClaimDate('');
    setResponseDueOn('');
    onRecorded();
  };
  return (
    <Card title={t('hr.claims.new')} density="plain">
      <div className={styles.fields} data-claims-new="true">
        {/* 🔨 T3622: the three short answers share a row and wrap on a phone, rather than one field to a row. */}
        <div className={styles.fieldRow}>
          <TextInput size={Size.Small} label={t('hr.claims.state')} value={stateCode} onChange={setStateCode} name="claimState" required />
          <DatePicker size={Size.Small} label={t('hr.claims.claimDate')} value={claimDate} onChange={setClaimDate} mode="date" name="claimDate" required />
          <DatePicker size={Size.Small} label={t('hr.claims.responseDueOn')} value={responseDueOn} onChange={setResponseDueOn} mode="date" name="claimResponseDueOn" required />
        </div>
        <p className={styles.meta}>{t('hr.claims.responseDueHint')}</p>
        {error ? <p className={styles.meta} role="alert" data-claims-error="true">{error}</p> : null}
        <div className={styles.controls}>
          <Button size={Size.Small} color={Color.Navy} text={[t('hr.claims.record')]} disabled={busy} actions={[() => void record()]} />
        </div>
      </div>
    </Card>
  );
}

/** One claim: its deadline and days left, the decision and the outcome to save, and its contest packet. */
function ClaimCard({ row, today, apiBaseUrl, onChanged, onRefused }: { row: UnemploymentClaimRow; today: string; apiBaseUrl: string; onChanged: (text: string) => void; onRefused: (text: string) => void }) {
  const [contestDecision, setContestDecision] = useState(row.contestDecision);
  const [contestReason, setContestReason] = useState(row.contestReason ?? '');
  const [outcome, setOutcome] = useState(row.outcome);
  const [outcomeOn, setOutcomeOn] = useState(row.outcomeOn ?? '');
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const format = useDateFormat();
  const day = (value: string): string => formatDateOnly(`${value}T00:00:00.000Z`, format);
  const overdue = claimOverdue(row, today);
  const daysLeft = claimDaysLeft(row.responseDueOn, today);
  const save = async () => {
    const update = { version: row.version, contestDecision, contestReason: contestReason.trim() || null, outcome, outcomeOn: outcomeOn || null };
    const problem = unemploymentClaimUpdateProblem(update);
    if (problem) return onRefused(problem);
    setBusy(true);
    const answer = await post<UnemploymentClaimsAnswer>(unemploymentClaimPath(row.id), update, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return onRefused(answer.message);
    onChanged(t('hr.claims.saved'));
  };
  const addFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0];
    event.target.value = '';
    if (!chosen) return;
    setBusy(true);
    const form = new FormData();
    form.append('file', chosen, chosen.name);
    const answer = await upload<UnemploymentClaimsAnswer>(unemploymentClaimPacketPath(row.id), form, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return onRefused(answer.message);
    onChanged(t('hr.claims.packetUploaded'));
  };
  return (
    <Card title={line(row.stateCode, '·', day(row.claimDate))} density="plain">
      <div className={styles.fields} data-claim={row.id} data-claim-overdue={String(overdue)}>
        <p className={overdue ? styles.overdue : styles.meta} data-claim-deadline={row.responseDueOn}>
          {line(t('hr.claims.responseDueOn'), day(row.responseDueOn), '·', overdue ? t('hr.claims.overdue') : line(Math.max(daysLeft, 0), t('hr.claims.daysLeft')))}
        </p>
        <p className={styles.meta}>{line(t('hr.claims.enteredBy'), row.createdByName)}</p>
        <div className={styles.fieldRow}>
          <Select size={Size.Medium} label={t('hr.claims.decision')} value={contestDecision} onChange={(next) => setContestDecision(next as typeof contestDecision)} options={UNEMPLOYMENT_CONTEST_DECISIONS.map((value) => ({ value, label: decisionLabel(value) }))} dataAttribute={{ 'data-claim-field': 'decision' }} />
          <Select size={Size.Medium} label={t('hr.claims.outcome')} value={outcome} onChange={(next) => setOutcome(next as typeof outcome)} options={UNEMPLOYMENT_CLAIM_OUTCOMES.map((value) => ({ value, label: outcomeLabel(value) }))} dataAttribute={{ 'data-claim-field': 'outcome' }} />
          <DatePicker size={Size.Small} label={t('hr.claims.outcomeOn')} value={outcomeOn} onChange={setOutcomeOn} mode="date" name={`claimOutcomeOn-${row.id}`} />
        </div>
        <Textarea size={Size.Large} label={t('hr.claims.reason')} value={contestReason} onChange={setContestReason} name={`claimReason-${row.id}`} />
        <div className={styles.controls}>
          <Button size={Size.Small} color={Color.Navy} text={[t('hr.claims.save')]} disabled={busy} actions={[() => void save()]} />
        </div>
        <p className={styles.heading}>{t('hr.claims.packet')}</p>
        {row.packet.length === 0 ? <p className={styles.meta} data-claim-packet-empty="true">{t('hr.claims.packetEmpty')}</p> : (
          <ul className={styles.list}>
            {row.packet.map((file) => (
              <li key={file.fileId} className={styles.item} data-claim-packet-file={file.fileId}>
                <a href={`${apiBaseUrl}/files/${file.fileId}/stream`} target="_blank" rel="noreferrer">{file.filename}</a>
              </li>
            ))}
          </ul>
        )}
        <div className={styles.controls}>
          <Button size={Size.Small} color={Color.Inactive} text={[t('hr.claims.packetUpload')]} disabled={busy} actions={[() => input.current?.click()]} />
          <input ref={input} className={styles.hiddenFile} type="file" accept="application/pdf,image/*" aria-label={t('hr.claims.packetUpload')} onChange={(event) => void addFile(event)} data-claim-packet-input={row.id} />
        </div>
      </div>
    </Card>
  );
}
